import React, { useEffect, useState, lazy, useCallback, useMemo, Suspense } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import * as XLSX from 'xlsx';
import Sidebar from "../components/Sidebar.jsx";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
// import DataGovernance from "./DataGovernance.jsx";
import { api, fetchRevenueByMonth, formatCurrency, formatNumber } from "./api.js";
import useFetch from "./useFetch.js";
import SalesByChannel from "./SalesByChannel.jsx";
import ChartCard from './ChartCard';
import {CustomerRanking, CustomerFamilyDrilldownModal} from "./CustomerRanking.jsx";
// import FamilyDrilldownModal from "./FamilyDrilldownModal.jsx";
import ProductRanking from "./ProductRanking.jsx";
import SalesByCategorySection from "./SalesByCategorySection.jsx";
import SalesByRegionSection from "./SalesByRegionSection.jsx";
// import WeeklyPlanner from './components/WeeklyPlanner.jsx';
import {COUNTRY_NAMES, COUNTRY_SHORT_NAMES, COUNTRY_STATES} from "./Countries.jsx";
  
import { DEFAULT_FILTERS, SectionSkeleton,ApiError, DataTable, getDefaultYTD, getPresetRange, formatToInputDate, formatDisplayDate, SectionCard, exportToExcel, LiveStatus,
   ConfidenceBar, Field, KpiRow, getYearColor } from '../utils/analyticsHelpers';

// Permissions APIs
import {
  getProfile,
  getUserPermissionByUser,
} from "../api/workOrder";

import "./Calendar.css";

const CustomCalendar = lazy(() => import("./Calender"));

// const [exportExcel, setExportExcel] = useState(false);

export default function AnalyticsDashboard() {
  const [activeView, setActiveView] = useState("analytics");

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900">
      <Sidebar activeView={activeView} onSelect={setActiveView} />
      <main className="flex-1 overflow-x-hidden">
        <SalesAnalyticsDashboard />
      </main>
    </div>
  );
}

/* =================== Sales Analytics Dashboard =================== */
const RevenueTooltip = ({ active, payload, label, availableYears, getYearColor, formatCurrency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-xl ring-1 ring-black/5">
        <div className="mb-2 border-b pb-1 text-[11px] font-black uppercase tracking-widest text-slate-400">
          {label} Breakdown
        </div>
        
        {/* Monthly Section */}
        <div className="mb-3 space-y-1">
          <div className="text-[9px] font-bold text-indigo-500 uppercase tracking-tighter mb-1">Monthly Revenue</div>
          {availableYears.map((year, idx) => (
            <div key={year} className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: getYearColor(idx) }} />
                <span className="text-[10px] font-bold text-slate-600">{year}</span>
              </div>
              <span className="text-[10px] font-black text-slate-900">
                {formatCurrency(payload[0]?.payload[year] || 0)}
              </span>
            </div>
          ))}
        </div>

        {/* YTD Section */}
        <div className="space-y-1 border-t pt-2">
          <div className="text-[9px] font-bold text-emerald-500 uppercase tracking-tighter mb-1">YTD Cumulative</div>
          {availableYears.map((year, idx) => (
            <div key={year} className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full opacity-50" style={{ background: getYearColor(idx) }} />
                <span className="text-[10px] font-bold text-slate-500">{year} YTD</span>
              </div>
              <span className="text-[10px] font-black text-slate-700">
                {formatCurrency(payload[0]?.payload[`${year}_ytd`] || 0)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};


function SalesAnalyticsDashboard() {
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DEFAULT_FILTERS);
  const [tick, setTick] = useState(0);
  const [customerGroupBy, setCustomerGroupBy] = useState("family");
  const [prodDrill, setProdDrill] = useState(null); 
  const [drilldownFamily, setDrilldownFamily] = useState(null);
  const [revViewMode, setRevViewMode] = useState('monthly'); 
  const [exportExcel, setExportExcel] = useState(false);

  useEffect(() => {
    
    const checkExportPermission = async () => {

      try {
        const response = await getProfile();

        console.log("User:", response);

        if (response && response.user) {
          const userPermRes = await getUserPermissionByUser(
            response.user.id // use the logged-in user's ID
          );
          console.log("Permissions:", userPermRes?.data?.permissions);
          // Adjust this condition according to your API response
          const hasExportPermission =
            userPermRes?.data?.permissions?.some(
              (permission) =>
                permission.resource === "ExportExcel" &&
                permission.allowed === true &&
                permission.access !== "no_access"
            );
            setExportExcel(!!hasExportPermission);          
        }
      } catch (error) {
        //console.error("Error checking export permission:", error);
        //setExportExcel(false);
      }
    };

    checkExportPermission();
  }, []);  

  console.log("Export Excel Permission:", exportExcel);

  // ✅ Callbacks for Drill-downs
  const handleProductDrillDown = useCallback((newParams) => {
    setProdDrill(newParams.family || "All Families");
  }, []);

  const handleFamilyDrillDown = useCallback((familyName, currentParams) => {
    console.log('Drilldown Triggered:', familyName);
    setDrilldownFamily({ name: familyName, params: currentParams });
  }, []);

  const handleCloseDrillDown = useCallback(() => {
    setDrilldownFamily(null);
  }, []);

  // ✅ Action Handlers
  const apply = () => {
    setApplied({ ...draft });
    setProdDrill(null); 
    setTick((t) => t + 1);
  };

  const reset = () => {
    const defaults = { ...DEFAULT_FILTERS, ...getDefaultYTD() };
    setDraft(defaults);
    setApplied(defaults);
    setProdDrill(null);
    setTick((t) => t + 1);
  };

  const refreshAll = () => setTick((t) => t + 1);

  // ✅ Memoized Params
  const params = useMemo(() => {
    const p = {};
    if (applied.from) p.from = applied.from;
    if (applied.to) p.to = applied.to;
    if (applied.family && applied.family !== "All Families") p.family = applied.family;
    if (applied.customer && applied.customer !== "All Customers") p.customer = applied.customer;
    if (applied.owner && applied.owner !== "All Owners") p.owner = applied.owner;
    if (applied.country && applied.country !== "All") p.country = applied.country;
    if (applied.region && applied.region !== "All Regions") p.region = applied.region;
    if (applied.channel && applied.channel !== "All Channel") p.channel = applied.channel;
    return p;
  }, [applied]);

  const productParams = useMemo(() => {
    const p = { ...params };
    if (prodDrill && prodDrill !== "All Families") p.family = prodDrill;
    return p;
  }, [params, prodDrill]);

  const depKey = `${applied.from}|${applied.to}|${applied.family}|${applied.customer}|${applied.owner}|${applied.country}|${applied.region}|${tick}`;
  const custDepKey = `${depKey}|${customerGroupBy}`;
  const prodDepKey = `${depKey}|${prodDrill}`;

  // ── API DATA FETCHING ───────────────────────────────────────
  const filtersQ = useFetch(() => api.filters(), []);
  const kpisQ = useFetch(() => api.kpis(params), [depKey]);  
  const channelQ = useFetch(() => api.salesByChannel(params), [depKey]);  

  const { data: revenueByMonth, loading: revMonthLoading, error: revMonthError } = 
    useFetch(() => fetchRevenueByMonth(params), [depKey]);

  // Transformation logic
  const revenueComparisonData = useMemo(() => {
    if (!revenueByMonth?.length) return [];
    const monthMap = {};
    revenueByMonth.forEach(item => {
      const mName = item.month.split(' ')[0]; 
      const year = item.year || item.month.split(' ')[1]; // Robust year check
      if (!monthMap[mName]) monthMap[mName] = { month: mName };
      monthMap[mName][year] = item.revenue;
      monthMap[mName][`${year}_ytd`] = item.cumulativeRevenue;
    });
    const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return monthOrder.map(m => monthMap[m] || { month: m });
  }, [revenueByMonth]);

  const availableYears = useMemo(() => {
    if (!revenueByMonth?.length) return [];
    return [...new Set(revenueByMonth.map(item => item.year || item.month.split(' ')[1]))]
      .sort((a, b) => Number(b) - Number(a));
  }, [revenueByMonth]);  

  // API fetches for sections
  const custQ = useFetch(() => api.customerRanking({ ...params, groupBy: customerGroupBy }), [custDepKey]);
  const prodQ = useFetch(() => api.productRanking(productParams), [prodDepKey]);
  const catQ  = useFetch(() => api.salesByCategory(params), [depKey]);
  const regionQ = useFetch(() => api.salesByRegion(params), [depKey]);  
  const fcastQ = useFetch(() => api.forecast(productParams), [prodDepKey]);

  return (
    <>
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="flex items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-sm">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-6" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-semibold leading-tight">Sales Analytics Dashboard</h1>
              <p className="text-xs text-slate-500">
                Top-level aggregation · <LiveStatus loading={kpisQ.loading} error={kpisQ.error} />
              </p>
            </div>
          </div>
          <button onClick={refreshAll} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" />
            </svg>
            Refresh All
          </button>
        </div>
      </header>

      <div className="space-y-6 p-6">
        <FilterBar draft={draft} setDraft={setDraft} apply={apply} reset={reset} options={filtersQ.data} />
        
        <KpiRow query={kpisQ} />

        {/* ── Section 01: Sales by Channel & Revenue Over Time ── */}
        <SectionCard
          number="01"
          title="Sales by Channel"
          subtitle="Revenue distribution across channels"          
          onExport={() => exportToExcel("sales-by-channel.csv", channelQ.data?.rows || [])}
          exportExcel = {exportExcel}
        >
          <SalesByChannel query={channelQ} />
          
          <div className="mt-8 space-y-5">
            <ChartCard 
              title={revViewMode === 'monthly' ? "Over Time Revenue Analysis" : "YTD Cumulative Progress"} 
              subtitle={revViewMode === 'monthly' ? "Monthly aggregated revenue" : "YTD aggregated revenue"}
              loading={revMonthLoading} 
              error={revMonthError} 
              height={380}
            >
              <div className="flex justify-end mb-4">
                <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 shadow-sm">
                  <button onClick={() => setRevViewMode('monthly')} className={`rounded-md px-4 py-1.5 text-[10px] font-black uppercase tracking-wider transition-all ${revViewMode === 'monthly' ? "bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-700"}`}>Monthly</button>
                  <button onClick={() => setRevViewMode('ytd')} className={`rounded-md px-4 py-1.5 text-[10px] font-black uppercase tracking-wider transition-all ${revViewMode === 'ytd' ? "bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-700"}`}>YTD</button>
                </div>
              </div>              
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<RevenueTooltip availableYears={availableYears} getYearColor={getYearColor} formatCurrency={formatCurrency} />} />
                  <Legend verticalAlign="top" content={() => (
                    <div className="flex justify-center gap-6 mb-8">
                      {availableYears.map((year, index) => (
                        <div key={year} className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ background: getYearColor(index) }} />
                          <span className="text-[10px] font-black text-slate-600 uppercase">{year}</span>
                        </div>
                      ))}
                    </div>
                  )} />
                  {availableYears.map((year, index) => (
                    <Line
                      key={year}
                      type="monotone"
                      dataKey={revViewMode === 'ytd' ? `${year}_ytd` : year}
                      name={year}
                      stroke={getYearColor(index)}
                      strokeWidth={revViewMode === 'ytd' ? 4 : 3}
                      dot={{ r: 3 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>            
            </ChartCard>
          </div>          
        </SectionCard>

        {/* ── Section 02: Customer Ranking ── */}
        <SectionCard
          number="02"
          title="Customer Ranking"
          subtitle={customerGroupBy === "family" ? "By Customer Families" : "By Individual Customers"}
          rightSlot={
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
              {[{ v: "family", label: "By Family" }, { v: "customer", label: "By Customer" }].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setCustomerGroupBy(opt.v)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition ${customerGroupBy === opt.v ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          }
        >
          
          <CustomerRanking 
            query={custQ} 
            groupBy={customerGroupBy} 
            onFamilyClick={handleFamilyDrillDown} 
            currentParams={params} 
          />
          
        </SectionCard>

        {/* ... Rest of the sections ... */}
        <SectionCard number="03" title={prodDrill && prodDrill !== "All Families" ? `Product Ranking - ${prodDrill}` : "Product Ranking"}>          
            <ProductRanking query={prodQ} onFilterChange={handleProductDrillDown} />          
        </SectionCard>

        <SectionCard number="04" title="Sales by Formats Families & Categories">            
            <SalesByCategorySection query={catQ} />                       
        </SectionCard> 

        <SectionCard number="05" title="Sales by Region">         
          <SalesByRegionSection query={regionQ} COUNTRY_NAMES={COUNTRY_NAMES} COUNTRY_STATES={COUNTRY_STATES} />
          
        </SectionCard>                 

        <SectionCard number="06" title="Trend Forecasting"> 
            <Forecast query={fcastQ} />         
        </SectionCard>

        <footer className="pt-2 pb-6 text-center text-xs text-slate-400">
          Sales Analytics · Aggregated view
        </footer>
      </div>

      {/* ✅ MANDATORY: The Drilldown Modal must be rendered here */}
      {drilldownFamily && (
        <CustomerFamilyDrilldownModal
          familyName={drilldownFamily.name}
          params={drilldownFamily.params}
          onClose={handleCloseDrillDown}
        />
      )}
    </>
  );
}

/* ============================== Filter Bar ============================== */
const selectCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-100 outline-none disabled:bg-slate-50";

function FilterBar({ draft, setDraft, apply, reset, options }) {
  const [activePreset, setActivePreset] = useState("YTD");
  const [dateFilter, setDateFilter] = useState("YTD");
  const [customDateRange, setCustomDateRange] = useState({
    start: draft.from ? new Date(draft.from) : null,
    end: draft.to ? new Date(draft.to) : null,
  });

  const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  const handlePresetClick = (preset) => {
    const range = getPresetRange(preset);
    setActivePreset(preset);
    setDateFilter(preset);
    setCustomDateRange(range);
    setDraft((prev) => ({
      ...prev,
      from: formatToInputDate(range.start),
      to: formatToInputDate(range.end),
    }));
  };

  const dateRangeChange = useCallback(
    (range) => {
      setCustomDateRange({ start: range.start, end: range.end });
      setDateFilter(range.filter || "custom");
      setActivePreset("");
      setDraft((prev) => ({
        ...prev,
        from: formatToInputDate(range.start),
        to: formatToInputDate(range.end),
      }));
    },
    [setDraft]
  );

  const handleChange = (key, value) => {
    if (key === "family") {
      setDraft({ ...draft, [key]: value, customer: "All Customers" });
    } else {
      setDraft({ ...draft, [key]: value });
    }
  };

  const handleReset = () => {
    reset();
    setActivePreset("YTD");
    setDateFilter("YTD");
    const range = getPresetRange("YTD");
    setCustomDateRange(range);
  };

  const families = options?.families || [];
  const selectedFamilyObj = families.find((f) => f.name === draft.family);
  const familyCustomers = selectedFamilyObj ? selectedFamilyObj.customers : [];

  const countries = options?.countries?.map((c) => COUNTRY_SHORT_NAMES[c] || c) || [];  
  const regions = options?.regionsByCountry?.[COUNTRY_NAMES[draft?.country || ""]] || [];
  const channels = options?.channels || [];

  return (
    <section className="h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col lg:flex-row gap-6">

        {/* Calendar Section */}
        <div className="lg:w-[340px] shrink-0">
          <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-1 flex items-center justify-between gap-1">
            {["MTD", "QTD", "YTD", "12M"].map((preset) => (
              <button
                key={preset}
                onClick={() => handlePresetClick(preset)}
                className={`flex-1 rounded-md py-1.5 text-xs font-bold transition ${activePreset === preset
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-white"
                  }`}
              >
                {preset}
              </button>
            ))}
          </div>

          <Suspense fallback={<div className="h-40 bg-slate-50 rounded-xl animate-pulse" />}>
            <CustomCalendar
              dateFilter={dateFilter}
              setDateFilter={setDateFilter}
              dateRange={customDateRange}
              onDateRangeChange={dateRangeChange}
              value={customDateRange.start || new Date()}
            />
          </Suspense>

          {draft.from && draft.to && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2">
              <svg className="h-4 w-4 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-xs text-slate-700">
                {activePreset && (
                  <span className="font-bold text-indigo-700">{activePreset}: </span>
                )}
                <span className="font-medium">
                  {formatDisplayDate(draft.from)} → {formatDisplayDate(draft.to)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Filters Section */}
        <div className="flex-1 flex flex-col justify-between">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">

            <Field label="Family">
              <select
                value={draft.family}
                onChange={(e) => handleChange("family", e.target.value)}
                className={`${selectCls}`}
              >
                <option>All Families</option>
                {families
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((f) => (
                  <option key={f._id} value={f.name}>{f.name}</option>
                ))}
              </select>
            </Field>

            {draft.family !== "All Families" && (
              <Field label="Customer">
                <select
                  value={draft.customer}
                  onChange={(e) => handleChange("customer", e.target.value)}
                  className={`${selectCls} uppercase`}
                >
                  <option>All Customers</option>
                  {familyCustomers
                  .slice()
                  .sort((a, b) => a.localeCompare(b))
                  .map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Country">
              <select
                value={draft.country}
                onChange={set("country")}
                className={selectCls}
              >
                <option value="All">All</option>
                {countries
                  .slice()
                  .map((c) => (
                    <option key={c} value={c}>
                      {COUNTRY_NAMES[c] || c}
                    </option>
                  ))}
              </select>
            </Field>

            {regions.length > 0 && (
              <Field label="Region">
                <select
                  value={draft.region}
                  onChange={set("region")}
                  className={selectCls}
                  disabled={draft.country === "All"}
                >
                  <option>All Regions</option>
                  {regions
                  .slice()
                  // .sort((a, b) => a.region.localeCompare(b?.region || ""))
                  .map((r) => (
                    <option key={r?.detail || r.region } value={r.detail || r.region}>{r.region}</option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Channel">
              <select
                value={draft.channel || "All"}
                onChange={(e) => handleChange("channel", e.target.value)}
                className={selectCls}
              >
                <option value="All">All Channels</option>
                
                {channels?.map((item) => {
                  // Logic: If the channel name is SHOPIFY, use "The Store" as the value 
                  // to match your backend filter requirements.
                  const optionValue = item.channel === "SHOPIFY" ? "The Store" : item.channel;
                  const optionLabel = item.channel === "SHOPIFY" ? "Shopify" : item.channel;

                  return (
                    <option key={item.channel} value={optionValue}>
                      {optionLabel}
                    </option>
                  );
                })}
              </select>
            </Field>

          </div>

          <div className="mt-8 flex items-center justify-end gap-3 border-t pt-5">
            <button
              onClick={handleReset}
              className="rounded-lg border border-slate-200 px-5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
            >
              Reset All
            </button>
            <button
              onClick={apply}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 transition"
            >
              <span>⟳</span> Apply Filters
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Forecast({ query }) {
  const isLoading = query.loading;
  const data = query.data || {};
  const forecastRows = data.rows || []; // Using the 'rows' key from your updated API
  const hasData = forecastRows.length > 0;

  if (query.error) return <ApiError error={query.error} label="Forecast" />;

  return (
    <div className="space-y-4">
      {isLoading && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-amber-500 animate-pulse uppercase tracking-wider">
            • Syncing Multi-Year Forecast...
          </span>
        </div>
      )}

      {isLoading && !hasData ? (
        <SectionSkeleton />
      ) : (
        <div className={`space-y-6 ${isLoading ? "opacity-50" : ""}`}>
          <DataTable
            columns={[
              { key: "period", label: "PERIOD" },
              { key: "revenue", label: "FORECAST REVENUE", align: "right" },
              { key: "units", label: "FORECAST UNITS", align: "right" },
              { key: "growth", label: "YOY TREND", align: "right" },
              { key: "confidence", label: "CONFIDENCE", align: "right" },
            ]}
            rows={forecastRows.map((f) => ({
              period: (
                <div className="flex flex-col">
                  <span className="font-black uppercase text-[11px] text-slate-800">
                    {f.period}
                  </span>
                  {/* Optional: Show which year it's comparing against */}
                  <span className="text-[8px] font-bold text-slate-400 uppercase">
                    {f.comparisonYear}
                  </span>
                </div>
              ),
              revenue: (
                <span className="font-black text-slate-900">
                  {formatCurrency(f.forecastRevenue)}
                </span>
              ),
              units: (
                <span className="font-bold text-slate-600">
                  {formatNumber(f.forecastUnits)}
                </span>
              ),
              growth: (
                <div className="flex flex-col items-end">
                  <div
                    className={`text-[11px] font-black flex items-center gap-1 ${
                      f.isPositive ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {f.isPositive ? (
                      <TrendingUp size={12} strokeWidth={3} />
                    ) : (
                      <TrendingDown size={12} strokeWidth={3} />
                    )}
                    {f.yoyTrend}
                  </div>
                </div>
              ),
              confidence: (
                <div className="flex items-center justify-end gap-3 min-w-[120px]">
                  <div className="w-24">
                     <ConfidenceBar pct={parseInt(f.confidence)} />
                  </div>
                  <span className="text-[10px] font-black text-slate-500 w-8">
                    {f.confidence}
                  </span>
                </div>
              ),
            }))}
          />
        </div>
      )}
      
      {!isLoading && hasData && data.summary && (
        <div className="px-4 py-2 bg-slate-50 rounded-lg border border-slate-100 flex justify-between items-center">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Data Source: {data.summary.dataRange}
          </span>
          <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">
            Trajectory: {data.summary.growthTrajectory}
          </span>
        </div>
      )}
    </div>
  );
}